import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
} from "@mui/material";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import dayjs, { Dayjs } from "dayjs";
import "./new-invitation-form.css";
import NewInvitationVisitorData from "../DesktopInvitationFormElements/NewInvitationVisitorData";
import NewInvitationValidity from "../DesktopInvitationFormElements/NewInvitationValidity";
import NewInvitationVisitDetails from "../DesktopInvitationFormElements/NewInvitationVisitDetails";
import { AccessGroupsData } from "../../api/models/access-groups";
import { selectUser, selectUserRestrictions } from "../../slices/user";
import api, {
  CreateAccessKeyRequest,
  CreateRequirementPayload,
  CreateVerifiableIdPayload,
  OneTimeVisitorPayload,
} from "../../api";
import IconifyIcon from "../IconifyIcon";
import NewInvitationProviderData, {
  ProviderInfo,
} from "../DesktopInvitationFormElements/NewInvitationProviderData/NewInvitationProviderData";
import { selectToken } from "../../slices/token";
import LoadingModal from "../Modals/LoadingModal";
import { OneTimeVisitorData } from "../../api/models/one-time-visitor";
import UserContacts from "../UserContacts";
import { UserContactsToShow } from "../UserContacts/UserContacts";
import { showError } from "../../utils/helpers";
import ExistedIdentifierModal from "../Modals/ExistedIdentifierModal";
import { checkInvitationAccessGroupCompatibility } from "../../utils/checkInvitationAccessGroupCompatibility";
import { AccessKeyCreateResponse } from "../../api/models/access-key-model";

const WEEK_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export interface RequirementValues {
  data: {
    requirement_type?: number;
    access_key_qr?: number;
    access_transaction?: number;
    url?: string;
    value: any;
  };
  id?: number;
  documentId?: string;
}

interface NewInvitationFormProps {
  extraFunc: () => void;
  openView: boolean;
  showContacts: boolean;
  setShowContacts: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  editingAccessKey?: AccessKeyCreateResponse | null;
}

const NewInvitationForm = ({
  extraFunc,
  openView,
  showContacts,
  setShowContacts,
  isMobile,
  editingAccessKey,
}: NewInvitationFormProps) => {
  const user = useAppSelector(selectUser);
  const labels = useAppSelector(selectLabels);
  const token = useAppSelector(selectToken);
  const userRestrictions = useAppSelector(selectUserRestrictions);
  const dispatch = useAppDispatch();

  const propertyId = user?.property?.id;

  const now = dayjs();
  const currentHour = now.format("HH");
  const currentMinutes = now.minute();

  const roundedMinutes = (() => {
    if (currentMinutes >= 45) return "45";
    if (currentMinutes >= 30) return "30";
    if (currentMinutes >= 15) return "15";
    return "00";
  })();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [invitationType, setInvitationType] = React.useState<
    "visitor" | "provider"
  >("visitor");
  const [multipleAccess, setMultipleAccess] = React.useState<boolean>(false);
  const [keyIdentifier, setKeyIdentifier] = React.useState<string>("");
  const [countryCode, setCountryCode] = React.useState<"+52" | "+1">("+52");
  const [visitorName, setVisitorName] = React.useState<string>("");
  const [visitorPhone, setVisitorPhone] = React.useState<string>("");
  const [visitorEmail, setVisitorEmail] = React.useState<string>("");
  const [saveContact, setSaveContact] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<Dayjs | null>(now);
  const [validUntil, setValidUntil] = React.useState<Dayjs | null>(now);
  const [intervalNumber, setIntervalNumber] = React.useState<string>("1");
  const [intervalTime, setIntervalTime] = React.useState<string>("days");
  const [hour, setHour] = React.useState<string>(currentHour);
  const [minutes, setMinutes] = React.useState<string>(roundedMinutes);
  const [hoursLater, setHoursLater] = React.useState<string>("1");
  const [weekDaysRestriction, setWeekDaysRestriction] = React.useState<
    string[]
  >([]);
  const [restrictions, setRestrictions] = React.useState<boolean>(false);
  const [selectedAccessPoints, setSelectedAccessPoints] = React.useState<
    string[]
  >([]);
  const [motive, setMotive] = React.useState<string>("");
  const [antipassback, setAntipassback] = React.useState<boolean>(false);
  const [accessGroups, setAccessGroups] = React.useState<AccessGroupsData[]>(
    [],
  );
  const [hasVisitorAccessGroups, setHasVisitorAccessGroups] =
    React.useState<boolean>(true);
  const [selectedProvider, setSelectedProvider] =
    React.useState<ProviderInfo | null>(null);
  const [otherProvider, setOtherProvider] = React.useState<string>("");
  const [successInvitationCreated, setSuccessInvitationCreated] =
    React.useState<boolean>(false);
  const [existedVisitor, setExistedVisitor] =
    React.useState<OneTimeVisitorData | null>(null);
  const [selectedContact, setSelectedContact] =
    React.useState<UserContactsToShow | null>(null);
  const [sendInvitation, setSendInvitation] = React.useState<boolean>(false);
  const [requirementValues, setRequirementValues] = React.useState<
    RequirementValues[]
  >([]);
  const [invUsers, setInvUsers] = React.useState<string>("0");
  const [allowAllDay, setAllowAllDay] = React.useState<boolean>(false);
  const [selectedAccessGroups, setSelectedAccessGroups] = React.useState<
    AccessGroupsData[] | null
  >(null);
  const [existedVisitorModal, setExistedVisitorModal] =
    React.useState<boolean>(false);
  const [existedInvitation, setExistedInvitation] =
    React.useState<boolean>(false);

  // const newVisitorEmail = `user_${
  //   visitorName.split(" ")[0]
  // }${Date.now()}@example.com`;
  // const newVisitorPassword = Math.random().toString(36).slice(-8);

  const invitationTypeArray = [
    {
      label: labels.visitor,
      value: "visitor",
    },
    {
      label: labels.provider,
      value: "provider",
    },
  ];

  const defaultValues = () => {
    setVisitorEmail("");
    setVisitorPhone("");
    setVisitorName("");
    setMotive("");
    setDate(now);
    setAntipassback(false);
    setMultipleAccess(false);
    setCountryCode("+52");
    setSaveContact(false);
    setIntervalNumber("1");
    setValidUntil(now);
    setIntervalTime("days");
    setHour(currentHour);
    setMinutes(roundedMinutes);
    setHoursLater("1");
    setWeekDaysRestriction([]);
    setRestrictions(false);
    setSelectedProvider(null);
    setOtherProvider("");
    setExistedVisitor(null);
    setKeyIdentifier("");
    setAllowAllDay(false);
    setExistedInvitation(false);
    setShowContacts(false);
    // Campos adicionales que se llenan durante la edición
    setInvitationType("visitor");
    setInvUsers("0");
    setRequirementValues([]);
    setSelectedAccessGroups(accessGroups);
  };

  React.useEffect(() => {
    if (selectedContact) {
      setVisitorName(selectedContact?.name);
      if (selectedContact?.phone) {
        const fixedPhone = selectedContact?.phone.startsWith("+52")
          ? selectedContact?.phone.slice(3)
          : selectedContact?.phone.slice(2);
        setVisitorPhone(fixedPhone);
      }
      if (selectedContact?.email) {
        setVisitorEmail(
          selectedContact?.email.endsWith("passtrack.mx")
            ? ""
            : selectedContact?.email,
        );
      }
    }
  }, [selectedContact]);

  React.useEffect(() => {
    // Si el sheet se cierra (openView es false), limpiar los datos
    if (!openView) {
      defaultValues();
      return;
    }

    if (editingAccessKey) {
      const accessKey = editingAccessKey;

      if (accessKey?.access_key_type === "provider") {
        setInvitationType("provider");
      } else {
        setInvitationType("visitor");
      }

      setMultipleAccess(accessKey?.access_key_type === "frequent");

      const accessKeyConfiguration = (
        accessKey as AccessKeyCreateResponse & {
          configuration?: { week_days?: string[] } | { week_days?: string[] }[];
        }
      )?.configuration;
      const configuredWeekDays = Array.isArray(accessKeyConfiguration)
        ? accessKeyConfiguration?.[0]?.week_days
        : accessKeyConfiguration?.week_days;
      setWeekDaysRestriction(
        accessKey?.access_key_type === "frequent"
          ? configuredWeekDays && configuredWeekDays.length > 0
            ? configuredWeekDays
            : WEEK_DAYS
          : [],
      );

      if (accessKey?.identifier) setKeyIdentifier(accessKey.identifier);

      if (accessKey?.one_time_visitor) {
        if (accessKey.one_time_visitor.name)
          setVisitorName(accessKey.one_time_visitor.name);
        if (accessKey.one_time_visitor.email)
          setVisitorEmail(accessKey.one_time_visitor.email);
        if (accessKey.one_time_visitor.phone) {
          const phone = accessKey.one_time_visitor.phone;
          if (phone.startsWith("+52")) {
            setCountryCode("+52");
            setVisitorPhone(phone.slice(3));
          } else if (phone.startsWith("+1")) {
            setCountryCode("+1");
            setVisitorPhone(phone.slice(2));
          } else {
            setVisitorPhone(phone);
          }
        }
        if (accessKey.one_time_visitor.provider) {
          setSelectedProvider({
            key: `${accessKey.one_time_visitor.provider.id}-0`,
            id: accessKey.one_time_visitor.provider.id,
            label: accessKey.one_time_visitor.provider.name,
            img: "",
          });
        }
        if (accessKey.one_time_visitor.provider_name)
          setOtherProvider(accessKey.one_time_visitor.provider_name);
      } else if (accessKey?.verified_visitor) {
        if (accessKey.verified_visitor.name)
          setVisitorName(accessKey.verified_visitor.name);
        if (accessKey.verified_visitor.email)
          setVisitorEmail(accessKey.verified_visitor.email);
        if (accessKey.verified_visitor.phone) {
          const phone = accessKey.verified_visitor.phone;
          if (phone.startsWith("+52")) {
            setCountryCode("+52");
            setVisitorPhone(phone.slice(3));
          } else if (phone.startsWith("+1")) {
            setCountryCode("+1");
            setVisitorPhone(phone.slice(2));
          } else {
            setVisitorPhone(phone);
          }
        }
      }

      if (accessKey?.valid_from) {
        const validFromDate = dayjs(accessKey.valid_from);
        setDate(validFromDate);
        setHour(validFromDate.format("HH"));
        setMinutes(validFromDate.format("mm"));
      }

      if (accessKey?.valid_until) {
        const validUntilDate = dayjs(accessKey.valid_until);
        setValidUntil(validUntilDate);

        if (accessKey.valid_from) {
          const validFromDate = dayjs(accessKey.valid_from);
          const fromHour = validFromDate.hour();
          const fromMinute = validFromDate.minute();
          const fromSecond = validFromDate.second();
          const fromMillisecond = validFromDate.millisecond();

          const untilHour = validUntilDate.hour();
          const untilMinute = validUntilDate.minute();
          const untilSecond = validUntilDate.second();
          const untilMillisecond = validUntilDate.millisecond();

          const isStartOfDay =
            (fromHour === 0 || fromHour === 6) &&
            fromMinute === 0 &&
            fromSecond === 0 &&
            fromMillisecond === 0;
          const isEndOfDay =
            (untilHour === 23 || untilHour === 5) &&
            untilMinute === 59 &&
            untilSecond === 59;

          const fromTotalMinutes = fromHour * 60 + fromMinute;
          const untilTotalMinutes = untilHour * 60 + untilMinute;
          let diffMinutes = untilTotalMinutes - fromTotalMinutes;
          if (diffMinutes < 0) {
            diffMinutes = 24 * 60 + diffMinutes;
          }
          const isAlmost24Hours = diffMinutes >= 1430;

          if ((isStartOfDay && isEndOfDay) || isAlmost24Hours) {
            setAllowAllDay(true);
          } else {
            const hoursDiff = Math.round(diffMinutes / 60);
            const finalHours = Math.max(1, Math.min(12, hoursDiff));
            setHoursLater(finalHours.toString());
          }
        }
      }

      if (accessKey?.reason_for_visit) {
        setMotive(accessKey.reason_for_visit);
      }

      if (
        accessKey?.access_groups &&
        accessKey.access_groups.length > 0
      ) {
        setSelectedAccessGroups(accessKey.access_groups);
      }

      if (accessKey?.antipassback !== undefined) {
        setAntipassback(accessKey.antipassback > 0);
        if (accessKey.antipassback > 0) {
          setInvUsers(accessKey.antipassback.toString());
        }
      }
    } else {
      defaultValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openView, editingAccessKey]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setState: (state: boolean) => void,
  ) => {
    setState(event.target.checked);
  };

  const getAccessGroups = React.useCallback(async () => {
    if (token && propertyId) {
      try {
        const response = await api.visitorAccessGroupsRequest(propertyId);
        if (response) {
          const visitorAccGrp = response.data;
          if (visitorAccGrp.length > 0) {
            setAccessGroups(visitorAccGrp);
            setSelectedAccessGroups(visitorAccGrp);
          } else {
            setHasVisitorAccessGroups(false);
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  }, [dispatch, labels.generic_error, propertyId, token]);

  React.useEffect(() => {
    getAccessGroups();
  }, [getAccessGroups]);

  React.useEffect(() => {
    if (openView && editingAccessKey && accessGroups.length > 0) {
      const rawRequirements = editingAccessKey.requirements;
      // @ts-ignore
      const accessKeyRequirements =
        rawRequirements?.data ||
        (Array.isArray(rawRequirements) ? rawRequirements : []);

      const availableRequirements =
        accessGroups[0]?.requirement_types?.filter(
          (item) => item?.access_key,
        ) || [];

      if (availableRequirements.length > 0) {
        const newRequirementValues: any[] = [];

        availableRequirements.forEach((reqType, index) => {
          const existingReq = accessKeyRequirements.find((r: any) => {
            const rId =
              r.attributes?.requirement_type?.data?.id ||
              r.requirement_type?.data?.id ||
              r.requirement_type?.id ||
              r.requirement_type;
            return rId === reqType.id;
          });

          if (existingReq) {
            const reqAny = existingReq as any;
            const val =
              reqAny.attributes?.value !== undefined
                ? reqAny.attributes.value
                : reqAny.value;

            if (reqType.data_type === "date") {
              newRequirementValues[index] = {
                data: {
                  requirement_type: reqType.id,
                  value: dayjs(val),
                },
                id: existingReq.id,
                documentId: reqAny.attributes?.documentId ?? reqAny.documentId,
              };
            } else {
              newRequirementValues[index] = {
                data: {
                  requirement_type: reqType.id,
                  value: val,
                },
                id: existingReq.id,
                documentId: reqAny.attributes?.documentId ?? reqAny.documentId,
              };
            }
          }
        });
        setRequirementValues(newRequirementValues);
      }
    }
  }, [openView, editingAccessKey, accessGroups]);

  const requiredRequirementsFilled = React.useMemo(() => {
    const availableRequirements =
      accessGroups[0]?.requirement_types?.filter(
        (item) => item?.access_key,
      ) || [];
    const requiredIndices = availableRequirements
      .map((req, index) => (req?.required ? index : -1))
      .filter((i) => i >= 0);
    if (requiredIndices.length === 0) return true;
    return requiredIndices.every((index) => {
      const val = requirementValues[index]?.data?.value;
      if (val === undefined || val === null) return false;
      if (typeof val === "string" && val.trim() === "") return false;
      return true;
    });
  }, [accessGroups, requirementValues]);
  React.useEffect(() => {
    setWeekDaysRestriction((prev) => {
      if (multipleAccess) {
        return prev.length > 0 ? prev : WEEK_DAYS;
      }
      return prev.length > 0 ? [] : prev;
    });
  }, [multipleAccess]);

  const accessGroupsForCompatibility = selectedAccessGroups ?? accessGroups;

  const invitationAccessCompatibility = React.useMemo(
    () =>
      checkInvitationAccessGroupCompatibility({
        multipleAccess,
        allowAllDay,
        date,
        hour,
        minutes,
        hoursLater,
        weekDaysRestriction,
        accessGroups: accessGroupsForCompatibility,
      }),
    [
      allowAllDay,
      date,
      hour,
      hoursLater,
      minutes,
      multipleAccess,
      accessGroupsForCompatibility,
      weekDaysRestriction,
    ],
  );

  const disabledButton = !multipleAccess
    ? !date ||
      isLoading ||
      !visitorName ||
      !motive ||
      !requiredRequirementsFilled ||
      !invitationAccessCompatibility.compatible
    : !date ||
      !validUntil ||
      isLoading ||
      !visitorName ||
      !motive ||
      !requiredRequirementsFilled ||
      weekDaysRestriction.length === 0 ||
      !invitationAccessCompatibility.compatible;

  const onPressCreate = () => {
    if (editingAccessKey) {
      // Si se está editando, actualizar primero el visitante si es necesario
      const existingVisitorId =
        editingAccessKey.one_time_visitor?.id ||
        editingAccessKey.verified_visitor?.id;
      const existingVisitorDocumentId =
        editingAccessKey.one_time_visitor?.documentId;
      if (
        existingVisitorId &&
        existingVisitorDocumentId &&
        editingAccessKey.one_time_visitor
      ) {
        // Verificar si hay cambios en los datos del visitante
        const hasVisitorChanges =
          visitorName !== editingAccessKey.one_time_visitor.name ||
          visitorEmail !== (editingAccessKey.one_time_visitor.email || "") ||
          `${countryCode}${visitorPhone}` !==
            editingAccessKey.one_time_visitor.phone;

        if (hasVisitorChanges) {
          updateVisitorUserForEdit(existingVisitorDocumentId, existingVisitorId);
        } else {
          createInvitation(existingVisitorId);
        }
      } else if (existingVisitorId) {
        createInvitation(existingVisitorId);
      }
    } else if (visitorPhone && countryCode) {
      const fullPhone = `${countryCode}${visitorPhone}`;
      getExistedVisitorUser(fullPhone);
    } else if (selectedProvider || otherProvider) {
      createVisitorUser();
    } else {
      createVisitorUser();
    }
  };

  const getExistedVisitorUser = async (visitorPhone: string) => {
    if (visitorPhone) {
      try {
        setIsLoading(true);
        const existedUserResponse = await api.getVisitorsByUser(
          `${user?.id}`,
          visitorPhone,
        );
        if (existedUserResponse?.data.length > 0) {
          setExistedVisitor(existedUserResponse?.data[0]);
          if (
            visitorEmail !== existedUserResponse?.data[0]?.email ||
            visitorName !== existedUserResponse?.data[0]?.name
          ) {
            setExistedVisitorModal(true);
          } else {
            createInvitation(existedUserResponse?.data[0]?.id);
          }
        } else {
          createVisitorUser();
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const createVisitorUser = async () => {
    let payload: OneTimeVisitorPayload = {
      data: {
        name: visitorName,
        email: visitorEmail || "",
        phone: countryCode + visitorPhone || "",
        created_by_user: Number(user?.id),
      },
    };
    if (selectedProvider) {
      if (selectedProvider?.id !== 0) {
        payload = {
          data: {
            provider: selectedProvider?.id,
            created_by_user: Number(user?.id),
          },
        };
      } else {
        if (otherProvider) {
          payload = {
            data: {
              provider_name: otherProvider,
              created_by_user: Number(user?.id),
            },
          };
        }
      }
    }
    try {
      setIsLoading(true);
      const createUserResponse = await api.createOneTimeVisitor(payload);
      console.log("createUserResponse", createUserResponse);
      if (createUserResponse) {
        createInvitation(createUserResponse?.id);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateVisitorUser = async () => {
    if (existedVisitor) {
      let payload: OneTimeVisitorPayload = {
        data: {
          name: visitorName,
          email: visitorEmail,
          phone: countryCode + visitorPhone,
          created_by_user: Number(user?.id),
        },
      };
      try {
        setIsLoading(true);
        const updatedUserResponse = await api.updateOneTimeVisitor(
          existedVisitor?.documentId,
          payload,
        );
        if (updatedUserResponse && updatedUserResponse?.id) {
          createInvitation(updatedUserResponse?.id);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const updateVisitorUserForEdit = async (
    visitorDocumentId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    visitorId: number,
  ) => {
    let payload: OneTimeVisitorPayload = {
      data: {
        name: visitorName,
        email: visitorEmail || "",
        phone: countryCode + visitorPhone || "",
        created_by_user: Number(user?.id),
      },
    };
    if (selectedProvider) {
      if (selectedProvider?.id !== 0) {
        payload = {
          data: {
            provider: selectedProvider?.id,
            created_by_user: Number(user?.id),
          },
        };
      } else {
        if (otherProvider) {
          payload = {
            data: {
              provider_name: otherProvider,
              created_by_user: Number(user?.id),
            },
          };
        }
      }
    }
    try {
      setIsLoading(true);
      const updatedUserResponse = await api.updateOneTimeVisitor(
        visitorDocumentId,
        payload,
      );
      if (updatedUserResponse && updatedUserResponse?.id) {
        // Después de actualizar el visitante, actualizar el access key
        createInvitation(updatedUserResponse?.id);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      setIsLoading(false);
    }
  };

  const createInvitation = async (visitorId: number) => {
    if (keyIdentifier && propertyId) {
      const currentIdentifier = editingAccessKey?.identifier || "";
      const newIdentifier = keyIdentifier;
      const isIdentifierChanged = currentIdentifier !== newIdentifier;
      if (isIdentifierChanged) {
        const invitationByIdentifier = await api.getInvitationByIdentifier(
          keyIdentifier,
          propertyId,
        );
        if (invitationByIdentifier.length > 0) {
          setExistedInvitation(true);
          return;
        }
      }
    }
    if (visitorId || editingAccessKey) {
      const finalValidFrom = allowAllDay
        ? dayjs(date).hour(0).minute(0).second(0).millisecond(0).toISOString()
        : dayjs(date)
            .hour(Number(hour))
            .minute(Number(minutes))
            .second(0)
            .millisecond(0)
            .toISOString();

      const finalValidUntil = allowAllDay
        ? dayjs(multipleAccess ? validUntil : date)
            .hour(23)
            .minute(59)
            .second(59)
            .millisecond(999)
            .toISOString()
        : multipleAccess
          ? dayjs(validUntil)
              .hour(Number(hour))
              .minute(Number(minutes))
              .add(Number(hoursLater), "hour")
              .second(0)
              .millisecond(0)
              .toISOString()
          : dayjs(date)
              .hour(Number(hour))
              .minute(Number(minutes))
              .add(Number(hoursLater), "hour")
              .second(0)
              .millisecond(0)
              .toISOString();

      const finalAntipassback = invUsers !== "0" ? Number(invUsers) : -1;
      let accessPointsIds: number[] = [];
      if (selectedAccessGroups) {
        accessPointsIds = [
          ...new Set(
            selectedAccessGroups.flatMap((item) =>
              item?.access_points?.map((ap) => ap?.id),
            ),
          ),
        ];
      } else {
        accessPointsIds = [
          ...new Set(
            accessGroups.flatMap((item) =>
              item?.access_points?.map((ap) => ap?.id),
            ),
          ),
        ];
      }
      const accessGroupsIds = selectedAccessGroups
        ? selectedAccessGroups.map((item) => item?.id)
        : [];
      const finalAccessKeyType =
        selectedProvider || otherProvider
          ? "provider"
          : multipleAccess
            ? "frequent"
            : "visitor";
      const payload: CreateAccessKeyRequest = {
        created: user?.id,
        one_time_visitor: visitorId,
        valid_from: finalValidFrom,
        valid_until: finalValidUntil,
        access_key_type: finalAccessKeyType,
        reason_for_visit: motive,
        property: user?.property?.id,
        unit: user?.unit?.id,
        access_points: accessPointsIds,
        access_groups: accessGroupsIds,
        antipassback: multipleAccess ? finalAntipassback : 1,
        access_counter: multipleAccess ? finalAntipassback : 1,
        identifier: keyIdentifier,
        exit_restriction: null,
        configuration:
          finalAccessKeyType === "frequent"
            ? {
                week_days:
                  weekDaysRestriction.length > 0
                    ? weekDaysRestriction
                    : WEEK_DAYS,
              }
            : undefined,
      };
      try {
        setIsLoading(true);

        if (editingAccessKey) {
          // Actualizar invitación existente
          const updatedInvitationResponse = await api.updateAccessKeyRequest(
            editingAccessKey.id,
            payload,
          );
          if (updatedInvitationResponse) {
            if (requirementValues.length > 0) {
              const accessKeyId = editingAccessKey.id;

              await Promise.all(
                requirementValues.map((req) => {
                  let val = req.data.value;
                  if (dayjs.isDayjs(val)) val = val.toISOString();
                  const requirementPayload: CreateRequirementPayload = {
                    data: {
                      ...req.data,
                      value: val,
                      access_key_qr: accessKeyId,
                      access_transaction: undefined,
                    },
                  };

                  if (req.documentId) {
                    return api.updateRequirement(
                      req.documentId,
                      requirementPayload,
                    );
                  } else {
                    return api.createRequirement(requirementPayload);
                  }
                }),
              );
            }
            // Cerrar el formulario después de actualizar
            extraFunc();
          }
        } else {
          // Crear nueva invitación
          const createdInvitationResponse =
            await api.createAccessKeyRequest(payload);
          if (createdInvitationResponse && createdInvitationResponse?.id) {
            const verifiablePayload: CreateVerifiableIdPayload = {
              data: {
                type: "qrcode",
                access_key_qr: createdInvitationResponse?.id,
              },
            };
            const verifiableIdResponse =
              await api.createVerifiableId(verifiablePayload);
            if (requirementValues.length > 0) {
              const accessKeyId = createdInvitationResponse.id;

              await Promise.all(
                requirementValues.map((req) => {
                  let val = req.data.value;
                  if (dayjs.isDayjs(val)) {
                    val = val.toISOString();
                  }
                  const requirementPayload: CreateRequirementPayload = {
                    data: {
                      ...req.data,
                      value: val,
                      access_key_qr: accessKeyId,
                      access_transaction: undefined,
                    },
                  };

                  return api.createRequirement(requirementPayload);
                }),
              );
            }
            if (verifiableIdResponse) {
              if (userRestrictions?.send_messages === true) {
                setSuccessInvitationCreated(true);
              } else {
                setSendInvitation(true);
              }
            }
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const createProviderInvitation = async () => {
    if (selectedProvider) {
      const accessPointsIds = [
        ...new Set(
          accessGroups.flatMap((item) =>
            item?.access_points?.map((ap) => ap?.id),
          ),
        ),
      ];
      const payload: CreateAccessKeyRequest = {
        created: user?.id,
        valid_from: dayjs(date).toISOString(),
        valid_until: multipleAccess
          ? dayjs(validUntil).toISOString()
          : dayjs(date).toISOString(),
        access_key_type: "provider",
        reason_for_visit: motive,
        property: user?.property?.id,
        unit: user?.unit?.id,
        access_points: accessPointsIds,
        provider: selectedProvider?.id,
        other_provider_name: otherProvider,
      };
      try {
        setIsLoading(true);
        const createdInvitationResponse =
          await api.createAccessKeyRequest(payload);
        if (createdInvitationResponse) {
          setSuccessInvitationCreated(true);
          setTimeout(() => {
            setSuccessInvitationCreated(false);
            extraFunc();
            defaultValues();
          }, 3000);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const sendEmail = () => {
    const email = "uriel.anaya@nimanic.com";
    const subject = "Asunto del mensaje";
    const body = "Hola, este es el cuerpo del email.";
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    onFinalizeCreateAccessKey();
  };

  const sendWhatsApp = () => {
    const phoneNumber = "7297376468";
    const message = "Hola, este es un mensaje desde mi aplicación.";
    const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message,
    )}`;
    window.open(whatsappLink, "_blank");
    onFinalizeCreateAccessKey();
  };

  const copyToClipboard = () => {
    const urlToCopy = "https://example.com";
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(urlToCopy)
        .then(() => {
          alert("¡URL copiada al portapapeles!");
        })
        .catch((err) => {
          console.error("Error al copiar al portapapeles:", err);
        });
      onFinalizeCreateAccessKey();
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = urlToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      alert("¡URL copiada al portapapeles (con fallback)!");
    }
  };

  const onFinalizeCreateAccessKey = () => {
    setSuccessInvitationCreated(false);
    setSendInvitation(false);
    extraFunc();
    defaultValues();
  };

  return (
    <div className="border=box flex gap-4">
      <ExistedIdentifierModal
        showExistedIdentifierModal={existedInvitation}
        setShowExistedIdentifierModal={setExistedInvitation}
        identifier={keyIdentifier || ""}
      />
      <div
        className="border-box flex flex-dir-col gap-4"
        style={{ width: isMobile ? "100%" : 660 }}
      >
        <LoadingModal open={isLoading} setOpen={setIsLoading} />
        <Modal
          open={successInvitationCreated}
          onClose={() => setSuccessInvitationCreated(false)}
        >
          <Box
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Paper
              style={{
                maxWidth: 350,
                minWidth: 350,
                width: "40%",
                boxSizing: "border-box",
                paddingTop: 20,
                paddingBottom: 20,
                paddingRight: 30,
                paddingLeft: 30,
              }}
            >
              {invitationType === "visitor" && (
                <>
                  {existedVisitor ? (
                    <>
                      <p className="t-nowrap body-text-lg font-w-500 title-color text-center">
                        {labels.sended_invitation}
                      </p>
                      <p className="t-nowrap body-text-md text-center mt-4">
                        {existedVisitor?.name}
                      </p>
                      <p className="t-nowrap body-text-sm text-center mt-10">
                        {labels.via_whatsapp}
                      </p>
                      <p className="t-nowrap body-text-md text-center mt-4">
                        {existedVisitor?.phone}
                      </p>
                      {existedVisitor?.email && (
                        <>
                          <p className="t-nowrap body-text-sm text-center mt-10">
                            {labels.via_email}
                          </p>
                          <p className="t-nowrap body-text-md text-center mt-4">
                            {existedVisitor?.email}
                          </p>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {!visitorEmail && !visitorPhone ? (
                        <>
                          <p className="t-nowrap body-text-lg font-w-500 title-color text-center">
                            {labels.created_invitation}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="t-nowrap body-text-lg font-w-500 title-color text-center">
                            {labels.sended_invitation}
                          </p>
                          <p className="t-nowrap body-text-md text-center mt-4">
                            {visitorName}
                          </p>
                          <p className="t-nowrap body-text-sm text-center mt-10">
                            {labels.via_whatsapp}
                          </p>
                          <p className="t-nowrap body-text-md text-center mt-4">
                            {`${countryCode}${visitorPhone}`}
                          </p>
                          {visitorEmail && (
                            <>
                              <p className="t-nowrap body-text-sm text-center mt-10">
                                {labels.via_email}
                              </p>
                              <p className="t-nowrap body-text-md text-center mt-4">
                                {visitorEmail}
                              </p>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <div className="w-100 flex justify-content-center mt-10">
                    <Button onClick={onFinalizeCreateAccessKey}>cerrar</Button>
                  </div>
                </>
              )}
              {invitationType === "provider" && (
                <>
                  <p className="t-nowrap body-text-lg font-w-500 title-color text-center">
                    {labels.created_invitation}
                  </p>
                  <div className="w-100 flex justify-content-center mt-10">
                    <Button onClick={onFinalizeCreateAccessKey}>cerrar</Button>
                  </div>
                </>
              )}
            </Paper>
          </Box>
        </Modal>
        <Modal
          open={existedVisitorModal}
          onClose={() => setExistedVisitorModal(false)}
        >
          <Box
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Paper
              style={{
                width: "90%",
                maxWidth: 450,
                minWidth: 350,
                boxSizing: "border-box",
                paddingTop: 20,
                paddingBottom: 20,
                paddingRight: 20,
                paddingLeft: 20,
              }}
            >
              <p className="body-text-lg font-w-500 title-color text-center mt-4">
                {"Ya tienes un contacto con este número telefónico"}
              </p>
              <div className="w-100 flex align-items-center justify-content-evenly mt-8">
                <p className="body-text-md subtitle-color text-center">
                  {"¿Quieres remplazar la información del contacto?"}
                </p>
              </div>
              <div
                className="w-100 mt-8 py-6 border-box"
                style={{ border: "1px #f1f1f1 solid", borderRadius: 6 }}
              >
                <div className="w-100 flex flex-dir-col align-items-center justify-content-evenly">
                  <p className="body-text-md subtitle-color text-center">
                    {existedVisitor?.name}
                  </p>
                  <p className="body-text-md subtitle-color text-center mt-2">
                    {existedVisitor?.phone}
                  </p>
                  <p
                    className={`body-text-md text-center mt-2 ${
                      existedVisitor?.email
                        ? "subtitle-color"
                        : "disabled-color"
                    }`}
                  >
                    {existedVisitor?.email
                      ? existedVisitor?.email
                      : "Sin correo electrónico"}
                  </p>
                </div>
                <div className="w-100 flex justify-content-center mt-8">
                  <IconifyIcon
                    icon={"mdi:arrow-down-bold"}
                    style={{ fontSize: 26 }}
                  />
                </div>
                <div className="w-100 flex flex-dir-col mt-8">
                  <p className="body-text-md subtitle-color text-center">
                    {visitorName}
                  </p>
                  <p className="body-text-md subtitle-color text-center mt-2">
                    {countryCode + visitorPhone}
                  </p>
                  <p
                    className={`body-text-md text-center mt-2 ${
                      visitorEmail ? "subtitle-color" : "disabled-color"
                    }`}
                  >
                    {visitorEmail ? visitorEmail : "Sin correo electrónico"}
                  </p>
                </div>
              </div>
              <div className="w-100 flex justify-content-center mt-10">
                <Button
                  variant="contained"
                  onClick={() => {
                    setExistedVisitorModal(false);
                    updateVisitorUser();
                  }}
                >
                  Guardar y continuar
                </Button>
              </div>
              <div className="w-100 flex justify-content-center mt-6 mb-4">
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (existedVisitor && existedVisitor?.id) {
                      setExistedVisitorModal(false);
                      createInvitation(existedVisitor?.id);
                    }
                  }}
                >
                  Continuar sin guardar cambios
                </Button>
              </div>
            </Paper>
          </Box>
        </Modal>
        <Modal open={sendInvitation} onClose={() => setSendInvitation(false)}>
          <Box
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Paper
              style={{
                maxWidth: 350,
                minWidth: 350,
                width: "40%",
                boxSizing: "border-box",
                paddingTop: 20,
                paddingBottom: 20,
                paddingRight: 20,
                paddingLeft: 20,
              }}
            >
              {invitationType === "visitor" && (
                <>
                  <p className="t-nowrap body-text-lg font-w-500 title-color text-center">
                    {labels.send_your_invitation}
                  </p>
                  <div className="w-100 flex align-items-center justify-content-evenly mt-12">
                    <Tooltip title={labels.copy} arrow placement="top">
                      <div>
                        <IconifyIcon
                          icon={"fa-solid:copy"}
                          className="primary-color pointer"
                          style={{ fontSize: 40 }}
                          onClick={copyToClipboard}
                        />
                      </div>
                    </Tooltip>
                    <Tooltip title={labels.send_email} arrow placement="top">
                      <div>
                        <IconifyIcon
                          icon={"fa-solid:envelope"}
                          className="primary-color pointer"
                          style={{ fontSize: 40 }}
                          onClick={sendEmail}
                        />
                      </div>
                    </Tooltip>
                    <Tooltip title={labels.whatsapp} arrow placement="top">
                      <div>
                        <IconifyIcon
                          icon={"streamline:whatsapp-solid"}
                          className="primary-color pointer"
                          style={{ fontSize: 40 }}
                          onClick={sendWhatsApp}
                        />
                      </div>
                    </Tooltip>
                  </div>
                  <div className="w-100 flex justify-content-center mt-10">
                    <Button onClick={onFinalizeCreateAccessKey}>
                      Continuar sin compartir
                    </Button>
                  </div>
                  <div className="w-100 flex justify-content-center mt-4">
                    <p className="body-text-sm text-center">
                      {labels.share_later}
                    </p>
                  </div>
                </>
              )}
              {invitationType === "provider" && (
                <>
                  <p className="t-nowrap body-text-lg font-w-500 title-color text-center">
                    {labels.created_invitation}
                  </p>
                  <div className="w-100 flex justify-content-center mt-10">
                    <Button onClick={onFinalizeCreateAccessKey}>cerrar</Button>
                  </div>
                </>
              )}
            </Paper>
          </Box>
        </Modal>
        {!hasVisitorAccessGroups ? (
          <div
            className="w-100 border-box hidden flex flex-dir-col align-items-center justify-content-between gap-10 p-8"
            style={{
              backgroundColor: "white",
              border: "1px #e0e0e0 solid",
              borderRadius: 6,
            }}
          >
            <div className="w-100 flex align-items-center gap-4">
              <IconifyIcon
                icon={"material-symbols:error"}
                style={{ color: "#bd190d" }}
              />
              <p
                className="t-nowrap body-text-lg font-w-500 title-color"
                style={{ color: "#bd190d" }}
              >
                {labels.not_acc_grp}
              </p>
            </div>
            <div className="w-100">
              <p className="body-text-sm subtitle-color mb-4">
                {labels.not_acc_grp_text}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div
              className={
                isMobile
                  ? "w-100 border-box hidden flex flex-dir-col align-items-center justify-content-between gap-10 p-8"
                  : "w-100 border-box hidden flex align-items-center justify-content-between gap-10 p-8"
              }
              style={{
                backgroundColor: "white",
                border: "1px #e0e0e0 solid",
                borderRadius: 6,
              }}
            >
              {/* <div className={isMobile ? "w-100" : "w-50"}> */}
              {/* <FormControl fullWidth>
                  <Select
                    value={invitationType}
                    onChange={(event: SelectChangeEvent) =>
                      setInvitationType(
                        event.target.value as "visitor" | "provider",
                      )
                    }
                    size="small"
                  >
                    {invitationTypeArray.map((item, index) => (
                      <MenuItem
                        key={`${index}-${item?.label}`}
                        value={item?.value}
                      >
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}
              {/* </div> */}
              <div
                className={
                  isMobile
                    ? "w-100 flex align-items-center justify-content-end gap-2"
                    : "w-100 flex align-items-center justify-content-end gap-2" //TODO: Change to w-50 when apply provider functionality
                }
              >
                {invitationType === "visitor" && (
                  <>
                    <p className="m-0 p-0 body-text-md">
                      {labels.multiple_access}
                    </p>
                    <Switch
                      checked={multipleAccess}
                      onChange={(e) => handleChange(e, setMultipleAccess)}
                    />
                  </>
                )}
              </div>
            </div>
            {invitationType === "visitor" && (
              <NewInvitationVisitorData
                visitorName={visitorName}
                setVisitorName={setVisitorName}
                countryCode={countryCode}
                setCountryCode={setCountryCode}
                visitorPhone={visitorPhone}
                setVisitorPhone={setVisitorPhone}
                visitorEmail={visitorEmail}
                setVisitorEmail={setVisitorEmail}
                saveContact={saveContact}
                setSaveContact={setSaveContact}
                isMobile={isMobile}
              />
            )}
            {invitationType === "provider" && (
              <NewInvitationProviderData
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                otherProvider={otherProvider}
                setOtherProvider={setOtherProvider}
                isMobile={isMobile}
              />
            )}
            <NewInvitationValidity
              multipleAccess={multipleAccess}
              restrictions={restrictions}
              setRestrictions={setRestrictions}
              date={date}
              setDate={setDate}
              hour={hour}
              setHour={setHour}
              minutes={minutes}
              setMinutes={setMinutes}
              hoursLater={hoursLater}
              setHoursLater={setHoursLater}
              intervalNumber={intervalNumber}
              setIntervalNumber={setIntervalNumber}
              intervalTime={intervalTime}
              setIntervalTime={setIntervalTime}
              weekDaysRestriction={weekDaysRestriction}
              setWeekDaysRestriction={setWeekDaysRestriction}
              validUntil={validUntil}
              setValidUntil={setValidUntil}
              isMobile={isMobile}
              allowAllDay={allowAllDay}
              setAllowAllDay={setAllowAllDay}
            />
            <NewInvitationVisitDetails
              motive={motive}
              setMotive={setMotive}
              multipleAccess={multipleAccess}
              antipassback={antipassback}
              setAntipassback={setAntipassback}
              selectedAccessPoints={selectedAccessPoints}
              setSelectedAccessPoints={setSelectedAccessPoints}
              accessGroups={accessGroups}
              requirementValues={requirementValues}
              setRequirementValues={setRequirementValues}
              isMobile={isMobile}
              invUsers={invUsers}
              setInvUsers={setInvUsers}
              selectedAccessGroups={selectedAccessGroups}
              setSelectedAccessGroups={setSelectedAccessGroups}
              identifier={keyIdentifier}
              setIdentifier={setKeyIdentifier}
            />
            <div
              className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
              style={{
                backgroundColor: "white",
                border: "1px #e0e0e0 solid",
                borderRadius: 6,
              }}
            >
              {!invitationAccessCompatibility.compatible &&
                invitationAccessCompatibility.hasAccessGroups && (
                  <Alert severity="warning" sx={{ width: "100%" }}>
                    <p className="body-text-sm m-0 mb-2">
                      {labels.invitation_access_schedule_mismatch}
                    </p>
                    <p className="body-text-xs m-0">
                      {labels.invitation_access_schedule_mismatch_hint}
                    </p>
                  </Alert>
                )}
              <div className="w-100" style={{ textAlign: "center" }}>
                <p className="t-nowrap body-text-sm">
                  * {labels.mandatory_fields}
                </p>
              </div>
              <div className="w-100" style={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={disabledButton}
                  onClick={onPressCreate}
                >
                  {isLoading ? (
                    <CircularProgress size={"small"} />
                  ) : editingAccessKey ? (
                    labels.save_changes
                  ) : (
                    labels.create_and_send_inv
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      {showContacts && (
        <div
          className="w-100 border-box hidden p-8"
          style={{
            width: 400,
            backgroundColor: "white",
            border: "1px #e0e0e0 solid",
            borderRadius: 6,
          }}
        >
          <div className="w-100">
            <div className="w-100">
              <p className="t-nowrap body-text-lg font-w-500 title-color mb-4">
                {labels.contacts}
              </p>
            </div>
            <div className="w-100 mt-10">
              <UserContacts setSelectedContact={setSelectedContact} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewInvitationForm;
